<template>
  <v-row v-if="!isProvider">
    <v-col v-if="mode == 'create' && !isStudent" cols="12">
      <div class="text-subtitle-1">
        Emergency Contact
      </div>
      <div class="text-subtitle-2">
        Please provide an emergency contact
      </div>
    </v-col>
    <v-col
      :cols="twoPerRowColumns"
      :class="mode == 'create' ? 'student-registration py-0' : 'mt-2'"
    >
      <v-text-field
        id="profile-contact-one-name"
        v-model="contact1Name"
        :label="mode == 'create' ? 'Emergency Contact Name' : 'Contact 1 Name'"
        prepend-icon="fa-light fa-address-card"
        type="text"
        required
        :error-messages="contact1NameErrorMessages"
        persistent-hint
        hint="Required - Must be an adult who is 18 years of age or older"
        @update:model-value="
          value => {
            contact1Name = value;
            $emit('update:contact1NameValidated', value);
            $methods.updateField(contact1Name, value);
        }"
        @blur="touchAllContactFields('contact1Name')"
      />
    </v-col>
    <v-col
      :cols="twoPerRowColumns"
      :class="mode == 'create' ? 'student-registration py-0' : 'mt-2'"
    >
      <InternationalPhoneInput
        id="profile-contact-one-phone"
        v-model="contact1Telephone"
        :enable-international="enableInternational"
        :available-countries="availableCountries"
        :label-text="
          isStudent
            ? 'Emergency Contact Phone Number'
            : 'Contact 1 Telephone Number'
        "
        prepend-icon
        required
        :error-messages="contact1TelephoneErrorMessages"
        persistent-hint
        :max-width="110"
        @update:error-messages="contact1TelephoneErrorMessages = $event"
        @blur="touchAllContactFields('contact1Telephone')"
        @touch="
          value => {
            contact1Telephone = value;
            $emit('update:contact1TelephoneValidated', value);
            $methods.updateField(contact1Telephone, value);
        }"
      />
    </v-col>
    <v-col v-if="mode !== 'create'" :cols="twoPerRowColumns">
      <v-text-field
        id="profile-contact-two-name"
        v-model="contact2Name"
        label="Contact 2 Name"
        prepend-icon="fa-light fa-address-card"
        type="text"
        persistent-hint
        hint="Optional - Must be an adult who is 18 years of age or older"
        @update:model-value="
          value => {
            contact2Name = value;
            $emit('update:contact2NameValidated', value);
            $methods.updateField(contact2Name, value);
          }"
        @blur="touchAllContactFields('contact2Name')"
      />
    </v-col>
    <v-col v-if="mode !== 'create'" :cols="twoPerRowColumns">
      <InternationalPhoneInput
        id="profile-contact-two-phone"
        v-model="contact2Telephone"
        label="Contact 2 Telephone Number"
        prepend-icon
        :error-messages="contact2TelephoneErrorMessages"
        hint=""
        persistent-hint
        :max-width="110"
        :enable-international="enableInternational"
        :available-countries="availableCountries"
        @update:error-messages="contact2TelephoneErrorMessages = $event"
        @blur="touchAllContactFields('contact2Telephone')"
        @keyup.enter="additionalFormValid ? nextStep(4) : null"
        @touch="
          value => {
            contact2Telephone = value;
            $emit('update:contact2TelephoneValidated', value);
            $methods.updateField(contact2Telephone, value);
        }"
      />
    </v-col>
  </v-row>
</template>
<script>
import { useVuelidate } from "@vuelidate/core";
import { uwillProfileCategoryMixin } from "./profile-category.js";
import { uwillProfileWrapperMixin } from "./profile-wrapper.js";
import InternationalPhoneInput from "@/components/form/InternationalPhoneInput.vue";

export default {
  name: "ProfileContact",
  components: {
    InternationalPhoneInput
  },
  mixins: [uwillProfileCategoryMixin, uwillProfileWrapperMixin],
  props: [
    "contact1NameValidated",
    "contact1TelephoneValidated",
    "contact2NameValidated",
    "contact2TelephoneValidated",
    "userTelephone",
    "isStudent"
  ],
  emits: [
    "update:contact1NameValidated",
    "update:contact1TelephoneValidated",
    "update:contact2NameValidated",
    "update:contact2TelephoneValidated"
  ],
  setup() {
    return { v$: useVuelidate() };
  },
  data: () => ({
    contact1Name: "",
    contact1Telephone: "",
    contact1TelephoneErrorMessages: [],
    contact2Name: "",
    contact2Telephone: "",
    contact2TelephoneErrorMessages: [],
    telephone: "",
    state: null,
    country: null
  }),
  computed: {
    contact1NameErrorMessages() {
      return this.requiredErrors("contact1Name");
    },
    // Rule for validating the phone number
    phoneNumberRule() {
      return value => {
        // Basic regex for phone number validation
        const phoneRegex = /^\s*(?:\+?(\d{1,3}))?[-.(\s]*(\d{1,4})[-.)\s]*(\d{3,5})[-. ]*(\d{3,6})$/;
        return phoneRegex.test(value) || 'Please enter a valid phone number';
      };
    }
  },
  watch: {
    contact1Telephone() {
      const validationErrors = this.phoneRequiredErrors(
        "contact1Telephone",
        true,
        this.duplicatePhoneErrors(this.contact1Telephone)
      );
      this.contact1TelephoneErrorMessages = [
        ...validationErrors,
        ...this.contact1TelephoneErrorMessages.filter(
          e => !validationErrors.includes(e)
        )
      ];
    },
    contact2Telephone() {
      const validationErrors = this.telephoneErrors(
        "contact2Telephone",
        true,
        this.duplicatePhoneErrors(this.contact2Telephone)
      );
      this.contact2TelephoneErrorMessages = [
        ...validationErrors,
        ...this.contact2TelephoneErrorMessages.filter(
          e => !validationErrors.includes(e)
        )
      ];
    }
  },
  mounted: function() {
    this.contact1Name = this.contact1NameValidated;
    this.contact1Telephone = this.contact1TelephoneValidated;
    this.contact2Name = this.contact2NameValidated;
    this.contact2Telephone = this.contact2TelephoneValidated;
    this.telephone = this.userTelephone;
  },
  methods: {
    // For some reason, updating a single field causes UI to send null for other fields
    // Not sure why, but touching all fields fixes this for now
    // Also, if statements prevent all the errors showing at once during registration
    touchAllContactFields(field) {
      if (field === "contact1Name" || this.contact1Name !== "") {
        this.v$.contact1Name.$touch();
      }
      if (field === "contact1Telephone" || this.contact1Telephone !== "") {
        this.v$.contact1Telephone.$touch();
      }

      if (field === "contact2Name" || this.contact2Name !== "") {
        this.v$.contact2Name.$touch();
      }
      if (field === "contact2Telephone" || this.contact2Telephone !== "") {
        this.v$.contact2Telephone.$touch();
      }
    }
  }
};
</script>
